/**
 * Dónde se guarda el idioma que eligió la persona.
 *
 * ── Por qué existe este archivo ─────────────────────────────────────────────
 * El panel guardaba su idioma sólo en `localStorage`, y `localStorage` no cruza
 * subdominios: alguien que dejaba el panel en inglés en `app.veltica.com` abría
 * el editor en `editor.veltica.com` y se lo encontraba en español. La cuenta
 * acaba corrigiéndolo —la preferencia vive también en `user_metadata`— pero
 * tarda lo que tarde la sesión en cargar, así que lo que se ve es el idioma
 * equivocado y luego un parpadeo.
 *
 * La solución ya estaba inventada en esta casa: es exactamente lo que hace el
 * color de acento en `@webbuilder/ui`. Cookie compartida en `.veltica.com` para
 * cruzar de subdominio, copia en `localStorage` para el primer pintado, y un
 * aviso para las demás pestañas. Aquí se repite esa mecánica, no se inventa otra.
 *
 * ── Y por qué vive en `@webbuilder/i18n` ────────────────────────────────────
 * Porque el idioma ya vive aquí: `Language`, `isLanguage` y `preferredLanguage`
 * son de este paquete. Guardarlo en otro sitio dejaría el tipo en un paquete y
 * su almacenamiento en otro, que es como acaban existiendo dos ideas distintas
 * de qué es un idioma válido.
 *
 * Esto guarda la preferencia; NO la aplica. Quién construye el traductor y con
 * qué catálogo es cosa de cada aplicación — el panel y el editor usan
 * `CATALOGS`, y lo que acaba dentro de una página publicada usa `SITE_CATALOGS`
 * con el idioma del PROYECTO, que no tiene nada que ver con esto.
 */
import { DEFAULT_LANGUAGE, isLanguage, preferredLanguage } from './language';
/** La copia local, para el primer pintado. La misma clave que ya usaba el panel. */
const STORAGE_KEY = 'veltica:language';
/** La compartida entre subdominios. Sin dos puntos: los evita toda cookie sana. */
const COOKIE = 'veltica_language';
/**
 * Lo que se anuncia al cambiar de idioma dentro de una misma página.
 *
 * Igual que con el acento: el idioma se puede cambiar desde más de un sitio y
 * quien no lo tocó tiene que enterarse, porque su idea de cuál está puesto la
 * leyó al montarse y nadie se la volvió a contar.
 */
export const LANGUAGE_CHANGE_EVENT = 'veltica:language';
function cookieLanguage() {
    if (typeof document === 'undefined')
        return null;
    const entry = document.cookie
        .split('; ')
        .find((part) => part.startsWith(`${COOKIE}=`));
    const value = entry?.slice(COOKIE.length + 1);
    return isLanguage(value) ? value : null;
}
/**
 * El elegido, o el que pida el navegador.
 *
 * La cookie manda sobre `localStorage`: es la que cruza subdominios, así que es
 * la única que puede saber que la persona cambió de idioma en el panel y acaba
 * de entrar al editor. `localStorage` va después para que una pestaña que
 * eligió idioma antes de que existiera la cookie no lo pierda.
 */
export function readLanguage() {
    const shared = cookieLanguage();
    if (shared)
        return shared;
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (isLanguage(stored))
            return stored;
    }
    catch {
        /* Ventana privada o almacenamiento bloqueado. No es un error: se cae a lo
           que diga el navegador, que es la siguiente mejor respuesta. */
    }
    /*
     * Lo que pida el navegador, y sólo si hay navegador de verdad.
     *
     * Se mira `document` y no `navigator`: Node trae su propio `navigator` desde
     * la versión 21, y dice inglés. Sin esta comprobación, cualquier código que
     * corra fuera del navegador —una prueba, un render en el servidor— se creería
     * que la persona quiere inglés cuando nadie lo ha pedido.
     */
    if (typeof document === 'undefined' || typeof navigator === 'undefined')
        return DEFAULT_LANGUAGE;
    return preferredLanguage(navigator.languages);
}
/**
 * Si la persona ELIGIÓ su idioma, o sólo se adivinó.
 *
 * `readLanguage` siempre devuelve uno, así que por sí solo no distingue «lo
 * puso ella» de «lo dedujimos del navegador». Y esa diferencia es la que decide
 * si se puede ofrecer un cambio: a quien ya eligió no se le vuelve a preguntar,
 * y a quien no, sugerirle algo no le quita nada.
 */
export function hasChosenLanguage() {
    if (cookieLanguage())
        return true;
    try {
        return isLanguage(localStorage.getItem(STORAGE_KEY));
    }
    catch {
        /* Ventana privada: no se sabe, así que se trata como «no eligió». Ofrecer
           de más es más barato que callar cuando hacía falta preguntar. */
        return false;
    }
}
/**
 * Lo guarda en los dos sitios y avisa.
 *
 * No toca el DOM ni construye traductores: eso es de quien llama. Aquí sólo se
 * decide dónde queda escrito.
 */
export function persistLanguage(language) {
    try {
        localStorage.setItem(STORAGE_KEY, language);
    }
    catch {
        /* La cookie sigue cubriendo la navegación normal entre subdominios. */
    }
    if (typeof document !== 'undefined' && typeof location !== 'undefined') {
        const domain = location.hostname === 'veltica.com' || location.hostname.endsWith('.veltica.com')
            ? '; Domain=.veltica.com'
            : '';
        const secure = location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${COOKIE}=${language}; Path=/; Max-Age=31536000; SameSite=Lax${domain}${secure}`;
    }
    if (typeof window !== 'undefined')
        window.dispatchEvent(new Event(LANGUAGE_CHANGE_EVENT));
}
/**
 * Avisa cada vez que el idioma cambia, aquí o en otra pestaña.
 *
 * `storage` cubre la segunda pestaña: allí `persistLanguage` no corrió, pero sí
 * escribió `localStorage`, y el navegador se lo cuenta a las demás. La cookie no
 * avisa de nada por su cuenta, así que sin esto una pestaña abierta se quedaría
 * en el idioma anterior hasta recargar.
 */
export function onLanguageChange(listener) {
    if (typeof window === 'undefined')
        return () => undefined;
    const onStorage = (event) => {
        if (event.key === STORAGE_KEY)
            listener();
    };
    window.addEventListener(LANGUAGE_CHANGE_EVENT, listener);
    window.addEventListener('storage', onStorage);
    return () => {
        window.removeEventListener(LANGUAGE_CHANGE_EVENT, listener);
        window.removeEventListener('storage', onStorage);
    };
}
//# sourceMappingURL=preference.js.map